import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DISTRICTS, getDistrictStats, fmtNum } from '../../data/districts';
import DemoBanner from '../../components/demo/DemoBanner';
import { Container, Card, Badge, BrandMark } from '../../components/ui';

const DistrictCard = ({ district }) => {
  const { primary, secondary, accent } = district.colors;
  const stats = getDistrictStats(district);
  const initials = district.shortName
    ? district.shortName.slice(0, 4)
    : district.name.slice(0, 2).toUpperCase();

  return (
    <Link to={`/demo/${district.slug}`} className="group block">
      <Card hover className="overflow-hidden flex flex-col h-full">
        <div
          className="h-24 relative flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
        >
          <div
            className="pointer-events-none absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-30 blur-2xl"
            style={{ backgroundColor: accent }}
          />
          <div
            className="relative w-14 h-14 rounded-2xl flex items-center justify-center font-extrabold text-lg shadow-lift ring-2 ring-white/25"
            style={{ backgroundColor: district.logo ? "#ffffff" : accent, color: primary }}
          >
            {district.logo ? (
              <img src={district.logo} alt="" className="w-full h-full object-contain rounded-2xl p-1.5" />
            ) : (
              initials
            )}
          </div>
        </div>
        <div className="p-5 flex flex-col flex-grow">
          <h3 className="font-bold font-display text-ink-900 text-lg leading-snug group-hover:text-brand-700 transition-colors">
            {district.name}
          </h3>
          <p className="text-sm text-ink-500 mt-0.5">📍 {district.city}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge tone="brand">{stats.schoolCount} schools</Badge>
            <Badge tone="neutral">{fmtNum(stats.ticketsYTD)} tickets/yr</Badge>
          </div>
          <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-600 group-hover:gap-2 transition-all">
            View demo →
          </span>
        </div>
      </Card>
    </Link>
  );
};

function DemoIndex() {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return DISTRICTS;
    return DISTRICTS.filter(
      (d) => d.name.toLowerCase().includes(q) || d.city.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-ink-50">
      <DemoBanner />

      {/* Hero */}
      <div className="relative overflow-hidden bg-brand-900 text-white">
        <div className="absolute inset-0 bg-brand-mesh opacity-80" />
        <Container className="relative py-14 sm:py-20 text-center">
          <BrandMark layout="stacked" className="h-16 sm:h-20 text-white mx-auto mb-6" />
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent-300 mb-3">
            Rio Grande Valley
          </span>
          <h1 className="text-3xl sm:text-5xl font-extrabold font-display leading-tight">
            District Showcase
          </h1>
          <p className="mt-4 text-base sm:text-lg text-brand-100 max-w-2xl mx-auto">
            See how Valley Tickets brings athletics, fine arts, and community
            events into one branded ticketing experience - built for each RGV district.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-md mx-auto">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search districts…"
              className="w-full px-5 py-3.5 rounded-2xl text-ink-800 text-base shadow-lift border-none outline-none focus:ring-4 focus:ring-accent-400/40 placeholder:text-ink-400"
            />
          </div>
        </Container>
      </div>

      {/* Grid */}
      <Container className="py-10 sm:py-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold font-display text-ink-900">
            {query ? `${filtered.length} result${filtered.length !== 1 ? 's' : ''}` : `All districts (${DISTRICTS.length})`}
          </h2>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center text-ink-400 py-16">
            No districts match "{query}".
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((d) => (
              <DistrictCard key={d.slug} district={d} />
            ))}
          </div>
        )}
      </Container>

      <footer className="border-t border-ink-200 bg-white">
        <Container className="py-8 text-center text-sm text-ink-400">
          <BrandMark layout="horizontal" className="h-8 mx-auto mb-3 text-ink-400" />
          Valley Tickets · School Districts · Athletics · Fine Arts · Community Events
        </Container>
      </footer>
    </div>
  );
}

export default DemoIndex;
